import { Chart, type ChartType, type Color, type Plugin } from 'chart.js';
import { t } from 'i18next';

import { type ChartDataLike, hasPlottableValue } from '../hasPlottableValue';

interface NoDataPluginOptions {
    /**
     * For arc charts (pie, doughnut). A slice of 0 has no area, so a chart whose values
     * are all 0 draws nothing and counts as empty. On an axis chart a 0 is a drawn point.
     */
    zeroIsEmpty?: boolean;
}

/** Per-chart options, under `options.plugins.noData`. */
export interface NoDataPluginChartOptions {
    /** The message colour. The chart wrappers set the chart theme's text colour. */
    color?: string;
}

declare module 'chart.js' {
    interface PluginOptionsByType<TType extends ChartType> {
        noData?: NoDataPluginChartOptions;
    }
}

/** The part of a Chart.js chart the message needs; a `Chart` satisfies it. */
export interface NoDataTarget {
    data: ChartDataLike;
    ctx: Pick<
        CanvasRenderingContext2D,
        'save' | 'restore' | 'fillText' | 'textAlign' | 'textBaseline' | 'fillStyle'
    >;
    width: number;
    height: number;
    clear(): unknown;
}

function hasPositiveValue(data: ChartDataLike): boolean {
    return (data.datasets ?? []).some((dataset) =>
        (dataset.data ?? []).some((value) => typeof value === 'number' && value > 0)
    );
}

/**
 * Clears the canvas and draws "No data to display" when the chart has nothing to draw.
 * Axis charts follow the same rule as `MetricChart` (`hasPlottableValue`): zeros are
 * drawn, an empty or all-null chart gets the message.
 *
 * `color` defaults to the Chart.js text colour, which follows the theme. Without a fill the
 * canvas draws black, which disappears on the dark card.
 */
export function drawNoDataMessage(
    chart: NoDataTarget,
    { zeroIsEmpty = false }: NoDataPluginOptions = {},
    color: Color | undefined = undefined
): void {
    const hasData = zeroIsEmpty ? hasPositiveValue(chart.data) : hasPlottableValue(chart.data);
    if (hasData) return;

    const ctx = chart.ctx;
    chart.clear();
    ctx.save();
    ctx.textAlign = 'center';
    ctx.textBaseline = 'middle';
    const fill = color ?? Chart.defaults.color;
    // The default is scriptable in Chart.js' types; the app only ever sets a colour.
    if (typeof fill !== 'function') ctx.fillStyle = fill;
    ctx.fillText(t('common.labels.noDataToDisplay'), chart.width / 2, chart.height / 2);
    ctx.restore();
}

/**
 * The no-data message as a Chart.js plugin. Every chart wrapper in `components/charts`
 * passes it as an inline plugin, ahead of the caller's own plugins, so each chart has it by
 * default. It is not registered globally (see `registerCharts.ts`). `MetricChart` with a
 * `noDataMessage` never reaches it: it renders its own empty state instead of the chart.
 */
export function createNoDataPlugin<TType extends ChartType>(
    options: NoDataPluginOptions = {}
): Plugin<TType> {
    return {
        id: 'noData',
        afterDraw: (chart, _args, chartOptions: NoDataPluginChartOptions | undefined) =>
            drawNoDataMessage(chart, options, chartOptions?.color),
    };
}
